import { useQuery, useInfiniteQuery, type NotifyOnChangeProps } from '@tanstack/react-query'
import { api, type PropertiesInclude } from '@/api/client'

/**
 * React Query hooks for API data fetching
 */

/**
 * `include` is part of the query key so the default (active-only) list and
 * the Archived tab's list (properties.tsx) get separate cache entries
 * instead of one clobbering the other. Normalized to 'active' in the key
 * (even though the request itself omits the param by default -- see
 * api.getProperties) so callers that pass nothing and callers that
 * explicitly pass 'active' share one cache entry rather than two.
 */
export function useProperties(include?: PropertiesInclude) {
  return useQuery({
    queryKey: ['properties', include ?? 'active'],
    queryFn: () => api.getProperties(include),
    staleTime: 30000, // 30 seconds
  })
}

export function usePropertyReservations(
  propertyId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-reservations', propertyId],
    queryFn: () => api.getPropertyReservations(propertyId ?? ''),
    staleTime: 30000,
    ...rest,
    enabled: !!propertyId && (enabledOption ?? true),
  })
}

export function useReservation(reservationId: string | null | undefined) {
  return useQuery({
    queryKey: ['reservation', reservationId],
    queryFn: () => api.getReservation(reservationId ?? ''),
    enabled: !!reservationId,
    staleTime: 30000,
  })
}

/**
 * PriceLabs nightly rates for a property's calendar month - best-effort:
 * many properties have no PriceLabs listing mapped yet, which the endpoint
 * reports as an ordinary (non-500) error response. Callers should treat a
 * failed/empty result as "no per-night prices to show" rather than surfacing
 * an error banner, since that's an expected, common state, not a bug.
 */
export function usePropertyPricingCalendar(
  propertyId: string | null | undefined,
  { year, month }: { year?: number; month?: number } = {}
) {
  return useQuery({
    queryKey: ['property-pricing-calendar', propertyId, year, month],
    queryFn: () => api.getPropertyPricingCalendar(propertyId ?? '', { year, month }),
    enabled: !!propertyId && !!year && !!month,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

interface UseReservationThreadOptions {
  refetchInterval?: number | false
}

export function useReservationThread(
  reservationId: string | null | undefined,
  options: UseReservationThreadOptions = {}
) {
  return useQuery({
    queryKey: ['reservation-thread', reservationId],
    queryFn: () => api.getReservationThread(reservationId ?? ''),
    enabled: !!reservationId,
    staleTime: 30000,
    refetchInterval: options.refetchInterval,
    ...options,
  })
}

/**
 * Minimal shape of the TanStack Query `Query` object passed into a
 * `refetchInterval` callback - just the fields useThreadMessages callers
 * actually read. `state.data` is deliberately `unknown` (not the real
 * thread-messages response shape, which lives in the still-untyped API
 * client) - callers narrow it to their own local "shape I need" interface
 * before reading fields off it.
 */
export interface ThreadMessagesRefetchQuery {
  state: {
    data?: unknown
    dataUpdateCount: number
    errorUpdateCount: number
  }
  queryKey: readonly unknown[]
}

export interface UseThreadMessagesOptions {
  refetchInterval?: number | false | ((query: ThreadMessagesRefetchQuery) => number | false)
  notifyOnChangeProps?: NotifyOnChangeProps
}

/**
 * `options.refetchInterval` lets a caller keep polling this thread while
 * some condition in its own data holds (e.g. the new Messages inbox route
 * polling while a suggestion is still queued/generating - see
 * src/pages/property-messages.jsx) - same passthrough pattern already used
 * by useReservationThread below. `options.notifyOnChangeProps` is also
 * passed through - a refetchInterval consumer tracking a bounded poll
 * budget needs a re-render on every settled fetch (see
 * property-messages.jsx's own comment on this, mirroring the identical
 * concern in suggested-review-block.jsx), not just when tracked-property
 * values happen to differ from the previous fetch.
 */
export function useThreadMessages(
  threadId: string | null | undefined,
  options: UseThreadMessagesOptions = {}
) {
  return useQuery({
    queryKey: ['thread-messages', threadId],
    queryFn: () => api.getThreadMessages(threadId ?? ''),
    enabled: !!threadId,
    staleTime: 30000,
    refetchInterval: options.refetchInterval,
    notifyOnChangeProps: options.notifyOnChangeProps,
  })
}

export function useAdminStatus() {
  return useQuery({
    queryKey: ['admin-status'],
    queryFn: () => api.getAdminStatus(),
    staleTime: 10000, // 10 seconds - status can change frequently
  })
}

export function useProperty(propertyId: string | null | undefined) {
  return useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => api.getProperty(propertyId ?? ''),
    enabled: !!propertyId,
    staleTime: 30000,
  })
}

export function usePropertyThreads(
  propertyId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-threads', propertyId],
    queryFn: () => api.getPropertyThreads(propertyId ?? ''),
    staleTime: 30000,
    ...rest,
    enabled: !!propertyId && (enabledOption ?? true),
  })
}

/**
 * Infinite-scroll pagination over a property's conversation threads,
 * most recently active first (GET /admin/properties/:id/threads/page).
 * Mirrors the useInfiniteQuery pattern already used by the logo-assets
 * tab (src/components/assets/logos-tab.jsx).
 *
 * `needsReply` must be part of the query key, not just `propertyId` -
 * switching the "Needs reply" filter needs to start a fresh pagination
 * chain against the server-filtered endpoint rather than reusing cached
 * unfiltered pages (filtering happens server-side, never over pages
 * already loaded on the client). `limit` is in the key too - it affects
 * the request and the resulting cursors, so a limit change must also
 * start a fresh chain instead of reusing pages fetched at a different
 * page size (Codex pre-PR review).
 */
export function usePropertyThreadsInfinite({
  propertyId,
  limit,
  needsReply = false,
}: {
  propertyId?: string | null
  limit?: number
  needsReply?: boolean
} = {}) {
  return useInfiniteQuery({
    queryKey: ['property-threads-page', propertyId, needsReply, limit],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      api.getPropertyThreadsPage({
        propertyId: propertyId ?? '',
        cursor: pageParam,
        limit,
        needsReply,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { nextCursor?: string | null }) =>
      lastPage.nextCursor ?? undefined,
    enabled: !!propertyId,
  })
}

/**
 * Infinite-scroll pagination over conversation threads across ALL
 * properties, most recently active first (GET /admin/threads/page) - backs
 * the admin portal-wide Inbox. Cross-property counterpart to
 * usePropertyThreadsInfinite above - same needsReply/limit-in-the-key
 * reasoning applies here too.
 */
export function useThreadsInfinite({
  limit,
  needsReply = false,
  enabled = true,
}: {
  limit?: number
  needsReply?: boolean
  enabled?: boolean
} = {}) {
  return useInfiniteQuery({
    queryKey: ['threads-page', needsReply, limit],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      api.getThreadsPage({ cursor: pageParam, limit, needsReply }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { nextCursor?: string | null }) =>
      lastPage.nextCursor ?? undefined,
    enabled,
  })
}

export function usePropertyAskSessions(
  propertyId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-ask-sessions', propertyId],
    queryFn: () => api.getPropertyAskSessions(propertyId ?? ''),
    staleTime: 10000,
    ...rest,
    enabled: !!propertyId && (enabledOption ?? true),
  })
}

export function usePropertyAskSession(
  propertyId: string | null | undefined,
  sessionId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-ask-session', propertyId, sessionId],
    queryFn: () => api.getPropertyAskSession(propertyId ?? '', sessionId ?? ''),
    staleTime: 10000,
    ...rest,
    enabled: !!propertyId && !!sessionId && (enabledOption ?? true),
  })
}

// Not fetched until a citation's "view conversation" is actually opened
// (enabled defaults to false) — most citations are never expanded, so
// fetching every cited thread up front would be wasted work.
export function usePropertyKnowledgeThread(
  propertyId: string | null | undefined,
  threadId: string | null | undefined,
  sourceType: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-knowledge-thread', propertyId, threadId, sourceType],
    queryFn: () =>
      api.getPropertyKnowledgeThread(propertyId ?? '', threadId ?? '', sourceType ?? ''),
    staleTime: 60000,
    ...rest,
    enabled: !!propertyId && !!threadId && !!sourceType && (enabledOption ?? false),
  })
}

export function usePropertyReviews(
  propertyId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-reviews', propertyId],
    queryFn: () => api.getPropertyReviews(propertyId ?? ''),
    staleTime: 30000,
    ...rest,
    enabled: !!propertyId && (enabledOption ?? true),
  })
}

export function usePropertyWeeklyEmails(
  propertyId: string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled: enabledOption, ...rest } = options
  return useQuery({
    queryKey: ['property-weekly-emails', propertyId],
    queryFn: () => api.getReportEmailHistory({ propertyId: propertyId ?? undefined }),
    staleTime: 30000,
    ...rest,
    enabled: !!propertyId && (enabledOption ?? true),
  })
}

export function useReservationThreadWithMessages(reservationId: string | null | undefined) {
  return useQuery({
    queryKey: ['reservation-thread-messages', reservationId],
    queryFn: () => api.getReservationThreadWithMessages(reservationId ?? ''),
    enabled: !!reservationId,
    staleTime: 30000,
  })
}
